import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { useFormContext } from "react-hook-form";
import type { AuthProviderCustom } from "@/app/oauth/types";
import { EnforcedResponseMapInput } from "./enforced-response-map-input";
import { RequestParametersInput } from "./request-parameters-input";

const INTROSPECTION_AUTH_METHOD_ITEMS = {
  client_secret_basic: "Client Secret Basic",
  bearer_access_token: "Bearer Access Token",
};

export const TokenIntrospectionFields = () => {
  const form = useFormContext<AuthProviderCustom>();
  const isEnabled = form.watch("oauth2.token_introspection_request.enabled");

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="oauth2.token_introspection_request.enabled"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <FormLabel>Enable Token Introspection</FormLabel>
              <FormDescription>
                If enabled, token introspection requests will be made to
                validate tokens
              </FormDescription>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />

      {!!isEnabled && (
        <>
          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.endpoint"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Introspection Endpoint
                  <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://example.oauth.com/services/oauth2/introspect"
                    {...field}
                    value={field.value || ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.method"
            render={({ field }) => (
              <FormItem>
                <FormLabel>HTTP Method</FormLabel>
                <Select
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select HTTP method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.auth_method"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Authentication Method</FormLabel>
                <Select
                  defaultValue={field.value}
                  items={INTROSPECTION_AUTH_METHOD_ITEMS}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select authentication method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="client_secret_basic">
                      Client Secret Basic
                    </SelectItem>
                    <SelectItem value="bearer_access_token">
                      Bearer Access Token
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.request_content_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Request Content Type</FormLabel>
                <Select
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select content type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="application/x-www-form-urlencoded">
                      application/x-www-form-urlencoded
                    </SelectItem>
                    <SelectItem value="application/json">
                      application/json
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.params"
            render={({ field }) => (
              <RequestParametersInput
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.response_content_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Response Content Type</FormLabel>
                <Select
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select content type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="application/x-www-form-urlencoded">
                      application/x-www-form-urlencoded
                    </SelectItem>
                    <SelectItem value="application/json">
                      application/json
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="oauth2.token_introspection_request.response_map"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <EnforcedResponseMapInput
                    form={form}
                    name={field.name}
                    onChange={field.onChange}
                    value={field.value || {}}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <FormLabel>Triggers</FormLabel>
            <FormField
              control={form.control}
              name="oauth2.token_introspection_request.triggers.on_token_grant"
              render={({ field }) => (
                <FormItem className="flex flex-col rounded-lg border p-4">
                  <div className="flex flex-row items-center justify-between">
                    <div className="space-y-0.5">
                      <FormLabel>On Token Grant</FormLabel>
                      <FormDescription>
                        Make introspection request when a token is granted
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="oauth2.token_introspection_request.triggers.on_token_refresh"
              render={({ field }) => (
                <FormItem className="flex flex-col rounded-lg border p-4">
                  <div className="flex flex-row items-center justify-between">
                    <div className="space-y-0.5">
                      <FormLabel>On Token Refresh</FormLabel>
                      <FormDescription>
                        Make introspection request when a token is refreshed
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};
